// One Activity entry: a Card meant to be a child of the tab's joined CardGroup. Layout
// follows the PR #6802 prototype row: a fixed icon column and a content column (type ∙
// subject header, shift lines, displaced worker, KPI tags, expandable reasons, timestamp).
// Built entirely on common primitives; the few values they can't express (fixed widths,
// optical nudge) are inline styles.
import React from 'react';
import {
  Card,
  Col,
  Collapse,
  Flex,
  Gap,
  Gutter,
  Icon,
  List,
  Row,
  Tag,
  Text,
} from '@common/components';
import { Spacing } from '@common/tokens';
import type { ScheduleMoveType } from '@load-balancing/types';
import {
  formatMoveTimestamp,
  getMoveShiftLines,
  getMoveSubject,
  getMoveTypeLabel,
} from './_schedule-review-activity-tab-service';

export function ScheduleReviewActivityTabCard({
  move,
  shiftLineById,
}: {
  move: ScheduleMoveType;
  shiftLineById: Record<string, string>;
}) {
  const subject = getMoveSubject(move);
  const { primary, secondary } = getMoveShiftLines(move, shiftLineById);
  const showKpis = move.status !== 'rejected';
  const actor =
    move.source === 'system' ? (move.editedBy ?? 'Works') : move.editedBy;
  const fteChanged =
    move.fteRemaining && move.fteRemaining.before !== move.fteRemaining.after;

  return (
    <Card className="schedule-review-activity-tab-card">
      <Gap size="sm" fullWidth>
        <div style={{ width: 20, flexShrink: 0, marginTop: 2 }}>
          <Flex align="center" justify="center">
            <Icon
              icon={move.source === 'system' ? 'sparkles' : 'user'}
              kind="light"
              color="midnightRegular"
              size="sm"
            />
          </Flex>
        </div>

        <Gap vertical size="sm" align="start" fullWidth>
          <Text size="md" color="midnightDarkest">
            <Text weight="medium">{getMoveTypeLabel(move)}</Text>
            {subject && (
              <>
                <Text> ∙ </Text>
                <Text>{subject}</Text>
              </>
            )}
          </Text>

          <Gap vertical size="sm" align="start" fullWidth>
            {move.kind === 'reassignment' ? (
              <Gap vertical size="xs" align="stretch" fullWidth>
                {secondary && (
                  <Row gutter={Spacing.xs}>
                    <Col span={3}>
                      <Text size="md" secondary>
                        From
                      </Text>
                    </Col>
                    <Col span={21}>
                      <Text size="md" color="midnightRegular">
                        {secondary}
                      </Text>
                    </Col>
                  </Row>
                )}
                {primary && (
                  <Row gutter={Spacing.xs}>
                    <Col span={3}>
                      <Text size="md" secondary>
                        To
                      </Text>
                    </Col>
                    <Col span={21}>
                      <Text size="md" color="midnightRegular">
                        {primary}
                      </Text>
                    </Col>
                  </Row>
                )}
              </Gap>
            ) : (
              primary && (
                <Text size="md" color="midnightRegular">
                  {primary}
                </Text>
              )
            )}

            {move.displacedWorkerName && (
              <Text size="md" secondary>
                Replaced {move.displacedWorkerName}
              </Text>
            )}

            {showKpis && (move.coverage || fteChanged) && (
              <Gutter direction="top">
                <Gap wrap>
                  {move.coverage && (
                    <Tag
                      kind="square"
                      color="grassLightest"
                      textColor="grassDarkest"
                    >
                      Coverage {move.coverage.before} → {move.coverage.after} of{' '}
                      {move.coverage.target}
                    </Tag>
                  )}
                  {fteChanged && (
                    <Tag
                      kind="square"
                      color="steelLighter"
                      textColor="midnightLighter"
                    >
                      {move.fteRemaining!.before} → {move.fteRemaining!.after}h
                      to FTE
                    </Tag>
                  )}
                </Gap>
              </Gutter>
            )}

            {move.reasons.length > 0 && (
              <Gutter direction="top" size="sm">
                <Collapse>
                  <Collapse.Panel
                    panelKey="reasons"
                    kind="inline"
                    title={
                      <Text bold color="elm">
                        Why this move?
                      </Text>
                    }
                  >
                    <List>
                      {move.reasons.map((reason) => (
                        <List.Item key={reason}>
                          <Text size="md" color="midnightRegular">
                            {reason}
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  </Collapse.Panel>
                </Collapse>
              </Gutter>
            )}

            <Text size="sm" secondary>
              {formatMoveTimestamp(move.createdAt, actor)}
            </Text>
          </Gap>
        </Gap>
      </Gap>
    </Card>
  );
}
